import { Navigate, useLocation } from 'react-router-dom';

import { useAuth, type ProgressPulseRole } from '@/lib/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ProgressPulseRole[];
  requirePlatformAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requirePlatformAdmin = false,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, mfaEnrollmentRequired } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
          aria-hidden="true"
        />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A7: staff whose org mandates MFA but who haven't enrolled are gated to the
  // Security page until they set it up. Learners never carry this flag.
  if (mfaEnrollmentRequired && location.pathname !== '/security') {
    return <Navigate to="/security" replace />;
  }

  if (requirePlatformAdmin && user?.role !== 'PlatformAdmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground mt-2">Platform administrator access required.</p>
          <a
            href="/dashboard"
            className="text-[color:var(--aia-blue)] hover:underline mt-4 inline-block"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role) && user.role !== 'PlatformAdmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground mt-2">
            You don&apos;t have the required role to view this page.
          </p>
          <a
            href="/dashboard"
            className="text-[color:var(--aia-blue)] hover:underline mt-4 inline-block"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
